import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert, Autocomplete,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const money = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.005) return "";
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const bal = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

export default function AccountLedger() {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/accounting/chart")
      .then((r) => setAccounts((r.data || []).map((a) => a.AccountName)))
      .catch(() => setAccounts([]));
  }, []);

  const run = useCallback(() => {
    if (!account) { setError("Pick an account"); return; }
    if (!from || !to) { setError("Pick a date range"); return; }
    setLoading(true); setError(null);
    api.get("/accounting/account-ledger", { params: { account, from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build account ledger"))
      .finally(() => setLoading(false));
  }, [account, from, to]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>General Ledger — Account Detail</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Every transaction that hit one account, with a running balance. Great for answering "what's in this account?"
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Autocomplete
            options={accounts} value={account} onChange={(_e, v) => setAccount(v)}
            sx={{ minWidth: 280 }} size="small"
            renderInput={(params) => <TextField {...params} label="Account" />}
          />
          <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{data.account}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {data.accountType}{data.subType ? ` · ${data.subType}` : ""} · {data.fromDate} to {data.toDate}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Debit</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Credit</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontStyle: "italic", color: "text.secondary" }}>Opening balance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{bal(data.opening)}</TableCell>
                </TableRow>
                {data.lines.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ color: "text.secondary" }}>No transactions in this range.</TableCell></TableRow>
                )}
                {data.lines.map((l, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ py: 0.4, whiteSpace: "nowrap" }}>{l.date}</TableCell>
                    <TableCell sx={{ py: 0.4, whiteSpace: "nowrap" }}>{l.source}</TableCell>
                    <TableCell sx={{ py: 0.4 }}>{l.descr}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{money(l.debit)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{money(l.credit)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{bal(l.balance)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800 }}>Totals / Closing</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money(data.totalDebit)}</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money(data.totalCredit)}</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{bal(data.closing)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Reconstructed from sale invoices (income), purchase invoices (COGS), Daily Cash (expenses), and journal
            entries. Cash, Receivable and Payable accounts show journal activity here — use Daily Cash, Aging, and the
            General Ledger (by party) for their full detail.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
